import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  Spinner,
  Stack,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Icon } from '@common/components/Icon';
import { useAutoInvestmentsModel } from '@common/models/AutoInvestmentsModel';
import currency from '@common/utility/currency';
import { FrequencyMap } from '@services/appsync/mappings';
import { AutoInvestScheduleV2 } from '@services/appsync/types';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { UI } from 'src/apps/invest/common/shells/UI';
import { ROUTES } from 'src/apps/invest/config';

export const AutoInvestmentsPage: React.FC = () => {
  const autoInvestModel = useAutoInvestmentsModel();
  const [processing, setProcessing] = useState<number | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef<any>();
  const toast = useToast();

  async function handleDelete(i: number, autoInvest: AutoInvestScheduleV2) {
    setProcessing(i);
    const result = await autoInvestModel.deleteAutoInvestment(autoInvest.autoinvest_id);
    if (result.success) {
      await autoInvestModel.fetchAutoInvestments();
      setProcessing(null);
      onClose();
    } else {
      toast({
        description: 'Something went wrong. Please try again. If the problem persists, please contact support.',
        duration: 5000,
        isClosable: true,
        status: 'error',
        title: 'Delete failed',
      });
      setProcessing(null);
      onClose();
    }
  }

  return (
    <UI>
      <Flex flexDirection={'column'} gap={'20px'}>
        <Box>
          <Heading fontSize={'24px'} textAlign={'left'}>
            Auto Investments
          </Heading>
        </Box>
        {autoInvestModel.autoInvestments.length === 0 && (
          <VStack w="full" gap="10px">
            <Stack
              flexDirection={['column', 'column', 'column', 'row']}
              w="full"
              justifyContent={'space-between'}
              alignItems={['center']}
              borderWidth={'1px'}
              borderColor={'gray.100'}
              borderRadius={'md'}
              gap="20px"
              p={'20px'}>
              <TextBlock color="gray.500" textAlign={['center', 'center', 'center', 'left']}>
                You have no auto investments.
              </TextBlock>
              <Button as={Link} to={ROUTES.checkoutCashflow} colorScheme="blue">
                Create Auto Investment
              </Button>
            </Stack>
          </VStack>
        )}
        <VStack w="full" gap="10px">
          {autoInvestModel.autoInvestments.map((autoInvest, i) => {
            const description = autoInvestModel.getFrequencyDescription({
              dayOfWeek:
                autoInvest.days_of_week && autoInvest.days_of_week.length > 0 ? autoInvest.days_of_week[0] : null,
              daysOfMonth: autoInvest.days_of_month ?? null,
              frequency: FrequencyMap[autoInvest.frequency as keyof typeof FrequencyMap],
            });
            const amount = `${currency.format(autoInvest.amount)}`;
            return (
              <HStack
                key={`auto-invest-${autoInvest.autoinvest_id}`}
                w="full"
                borderWidth={'1px'}
                borderColor={'gray.100'}
                borderRadius={'md'}
                justifyContent="space-between"
                gap={'20px'}
                p={'20px'}>
                <Box>
                  <TextBlock fontSize="12px" color="gray.500">
                    {description}
                  </TextBlock>
                  <TextBlock>{amount}</TextBlock>
                </Box>
                <HStack alignItems="center">
                  <Button
                    as={Link}
                    to={`${ROUTES.checkoutCashflow}?ai=${autoInvest.autoinvest_id}`}
                    colorScheme="blackAlpha"
                    variant={'ghost'}>
                    Edit
                  </Button>

                  <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
                    <AlertDialogOverlay>
                      <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                          Please Confirm
                        </AlertDialogHeader>

                        <AlertDialogBody>{`Are you sure you want to delete your ${currency.format(
                          autoInvest.amount,
                        )} auto investment (${description})?`}</AlertDialogBody>

                        <AlertDialogFooter>
                          <Button variant="ghost" colorScheme="blackAlpha" ref={cancelRef} onClick={onClose}>
                            Cancel
                          </Button>
                          <Button
                            variant="outline"
                            colorScheme="black"
                            onClick={() => {
                              handleDelete(i, autoInvest);
                            }}
                            ml={3}>
                            Delete
                          </Button>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialogOverlay>
                  </AlertDialog>
                  <IconButton
                    aria-label="Delete"
                    variant={'ghost'}
                    colorScheme="blackAlpha"
                    disabled={processing === i}
                    icon={
                      processing === i ? (
                        <Spinner size="sm" colorScheme="blackAlpha" />
                      ) : (
                        <Icon variant={'regular'} icon="trash-can"></Icon>
                      )
                    }
                    onClick={onOpen}></IconButton>
                </HStack>
              </HStack>
            );
          })}
        </VStack>
      </Flex>
    </UI>
  );
};
